import { useEffect, useRef } from 'react';
import ResearchToReality from './ResearchToReality';
import VMOSTFramework from './VMOSTFramework';
import birdLogoBlack from '../../../assets/matou-bird-logo-black(1).png';
import cityJungle from '../../../assets/city-jungle.webp';
import wakaMarae from '../../../assets/waka-marae.svg';

const panels = [
  {
    title: 'What We See',
    accentColor: '#EA6B66',
    description:
      'The colonial western system treats everything in the natural world as a resource to be extracted and turned into profit. The technology born from this worldview is no different — built to surveil, control, and extract. This way of living has set us all — people and planet — on a path of self-destruction.',
  },
  {
    title: 'What We Know',
    accentColor: '#82b366',
    description:
      "For millennia, Indigenous peoples have designed systems that prioritise collective wellbeing over extraction. These systems don't just sustain life — they regenerate it. Digital tools shaped by this wisdom can do the same.",
  },
  {
    title: 'Who We Are',
    accentColor: '#619793',
    image: birdLogoBlack,
    description: 'matou-custom',
  },
];

// Background: teal-dark -> slightly lighter teal -> white
const bgStops = [
  { r: 37, g: 61, b: 57 },    // #253D39
  { r: 47, g: 79, b: 71 },    // #2F4F47
  { r: 245, g: 245, b: 240 }, // #F5F5F0
];

// Text: white through first two panels, dark on final
const textStops = [
  { r: 220, g: 220, b: 215 },
  { r: 220, g: 220, b: 215 },
  { r: 60, g: 60, b: 55 },
];

function lerp(a: { r: number; g: number; b: number }, b: { r: number; g: number; b: number }, t: number) {
  return {
    r: Math.round(a.r + (b.r - a.r) * t),
    g: Math.round(a.g + (b.g - a.g) * t),
    b: Math.round(a.b + (b.b - a.b) * t),
  };
}

function interpolate(stops: { r: number; g: number; b: number }[], progress: number) {
  const segment = progress * (stops.length - 1);
  const idx = Math.min(Math.floor(segment), stops.length - 2);
  const t = segment - idx;
  const c = lerp(stops[idx], stops[idx + 1], t);
  return `rgb(${c.r}, ${c.g}, ${c.b})`;
}

export default function ProblemSolution() {
  const containerRef = useRef<HTMLDivElement>(null);
  const panelsRef = useRef<HTMLDivElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const cityJungleRef = useRef<HTMLImageElement>(null);
  const wakaMaraeRef = useRef<HTMLImageElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const dotsRef = useRef<(HTMLDivElement | null)[]>([]);
  const panelEls = useRef<(HTMLDivElement | null)[]>([]);
  const hintRef = useRef<HTMLDivElement>(null);
  const dotsContainerRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const totalPanels = panels.length;

  useEffect(() => {
    let rafId: number;

    function update() {
      const container = containerRef.current;
      const panelsEl = panelsRef.current;
      const bgEl = bgRef.current;
      const progressEl = progressRef.current;
      if (!container || !panelsEl || !bgEl || !progressEl) return;

      const rect = container.getBoundingClientRect();
      const rawProgress = Math.min(
        Math.max(-rect.top / (container.scrollHeight - window.innerHeight), 0),
        1
      );

      // Map raw scroll progress to visual progress with pauses at each panel.
      // 5 zones: move | pause | move | pause | move
      // Each pause holds the panel centered while the user keeps scrolling.
      const pauseFraction = 0.12; // portion of scroll spent paused per panel
      const moveFraction = (1 - pauseFraction * 3) / 2; // remaining split between 2 transitions
      // Breakpoints: [move0] [pause0] [move1] [pause1] [move2] [pause2] [end]
      const b0 = moveFraction * 0.5;                    // start of pause at panel 0 (halfway through first move is panel 0 center, but panel 0 starts at 0)
      // Actually: panel 0 is at visual 0, panel 1 at visual 0.5, panel 2 at visual 1
      // We want pauses at each panel center in scroll space:
      // pause at panel 0: 0 to pauseFraction
      // move to panel 1: pauseFraction to pauseFraction + moveFraction
      // pause at panel 1: pauseFraction + moveFraction to 2*pauseFraction + moveFraction
      // move to panel 2: 2*pauseFraction + moveFraction to 2*pauseFraction + 2*moveFraction
      // pause at panel 2: 2*pauseFraction + 2*moveFraction to 3*pauseFraction + 2*moveFraction = 1
      const p = rawProgress;
      let scrollProgress: number;

      const seg1End = pauseFraction;                          // end of pause at panel 0
      const seg2End = seg1End + moveFraction;                 // end of move to panel 1
      const seg3End = seg2End + pauseFraction;                // end of pause at panel 1
      const seg4End = seg3End + moveFraction;                 // end of move to panel 2
      // seg5End = seg4End + pauseFraction = 1               // end of pause at panel 2

      if (p <= seg1End) {
        scrollProgress = 0; // paused at panel 0
      } else if (p <= seg2End) {
        scrollProgress = ((p - seg1End) / moveFraction) * 0.5; // moving 0 -> 0.5
      } else if (p <= seg3End) {
        scrollProgress = 0.5; // paused at panel 1
      } else if (p <= seg4End) {
        scrollProgress = 0.5 + ((p - seg3End) / moveFraction) * 0.5; // moving 0.5 -> 1
      } else {
        scrollProgress = 1; // paused at panel 2
      }

      // Horizontal movement
      panelsEl.style.transform = `translateX(${-scrollProgress * (totalPanels - 1) * 100}vw)`;

      // Background
      bgEl.style.backgroundColor = interpolate(bgStops, scrollProgress);

      // Crossfade the two scroll illustrations so they don't overlap (esp. on
      // mobile where they're zoomed wider). Across the panel 1 -> panel 2 move:
      // city-jungle (mountains) fades 0.1 -> 0, waka-marae (Who We Are) fades 0 -> 0.1.
      const cityJungleOpacity = 0.1;
      const wakaMaraeOpacity = 0.18; // "Who We Are" sits a touch more visible
      const fade = Math.min(Math.max((scrollProgress - 0.5) / 0.5, 0), 1); // 0 at panel 1, 1 at panel 2
      if (cityJungleRef.current) cityJungleRef.current.style.opacity = (cityJungleOpacity * (1 - fade)).toString();
      if (wakaMaraeRef.current) wakaMaraeRef.current.style.opacity = (wakaMaraeOpacity * fade).toString();

      // Text colour
      const textCol = interpolate(textStops, scrollProgress);
      panelEls.current.forEach((el, i) => {
        if (!el) return;
        const pEl = el.querySelector<HTMLElement>('[data-body]');
        if (pEl) pEl.style.color = textCol;

        // Last panel title adapts
        if (i === 2) {
          const titleEl = el.querySelector<HTMLElement>('[data-title]');
          if (titleEl) titleEl.style.color = scrollProgress > 0.8 ? '#253D39' : '#619793';
        }
      });

      // Progress bar
      const isLight = scrollProgress > 0.75;
      progressEl.style.width = `${scrollProgress * 100}%`;
      progressEl.style.backgroundColor = isLight ? '#253D39' : '#619793';
      const barBg = progressEl.parentElement;
      if (barBg) barBg.style.backgroundColor = isLight ? 'rgba(37,61,57,0.1)' : 'rgba(255,255,255,0.1)';

      // Dots
      const panelProgress = scrollProgress * (totalPanels - 1);
      const activeIndex = Math.round(panelProgress);
      const dotActive = isLight ? '#253D39' : '#fff';
      const dotInactive = isLight ? 'rgba(37,61,57,0.25)' : 'rgba(255,255,255,0.25)';
      dotsRef.current.forEach((dot, i) => {
        if (!dot) return;
        const active = i === activeIndex;
        dot.style.backgroundColor = active ? dotActive : dotInactive;
        dot.style.transform = active ? 'scale(1.4)' : 'scale(1)';
      });

      // Parallax visibility
      panelEls.current.forEach((el, i) => {
        if (!el) return;
        const distance = Math.abs(panelProgress - i);
        if (distance < 0.4) {
          el.classList.add('ps-visible');
        } else {
          el.classList.remove('ps-visible');
        }
      });

      // Fade out controls at the end
      const endFade = rawProgress > 0.75 ? Math.max(1 - (rawProgress - 0.75) / 0.15, 0).toString() : '1';
      if (dotsContainerRef.current) dotsContainerRef.current.style.opacity = endFade;
      if (progressBarRef.current) progressBarRef.current.style.opacity = endFade;

      // Scroll hint
      if (hintRef.current) {
        hintRef.current.style.opacity = scrollProgress > 0.05 ? '0' : '1';
        hintRef.current.style.color = 'rgba(255,255,255,0.4)';
      }

      rafId = requestAnimationFrame(update);
    }

    rafId = requestAnimationFrame(update);
    return () => cancelAnimationFrame(rafId);
  }, [totalPanels]);

  function scrollToPanel(index: number) {
    const container = containerRef.current;
    if (!container) return;
    const maxScroll = container.scrollHeight - window.innerHeight;
    const containerTop = container.offsetTop;
    const targetScroll = containerTop + (index / (totalPanels - 1)) * maxScroll;
    window.scrollTo({ top: targetScroll, behavior: 'smooth' });
  }

  return (
    <>
      <section id="about" ref={containerRef} className="relative" style={{ height: '700vh' }}>
        <div className="sticky top-0 h-screen overflow-hidden">
          <div ref={bgRef} className="absolute inset-0" />
          <div
            ref={panelsRef}
            className="relative z-10 flex h-screen"
            style={{ width: `${totalPanels * 100}vw`, willChange: 'transform' }}
          >
            {/* City-jungle illustration spanning first two panels.
                Zoomed in on mobile (wider -> taller) but kept centered at 100vw;
                desktop sizing restored at md. */}
            <img
              ref={cityJungleRef}
              src={cityJungle}
              alt=""
              className="absolute bottom-0 pointer-events-none opacity-10 w-[270vw] left-[-35vw] md:w-[160vw] md:left-[20vw]"
              style={{ height: 'auto' }}
              aria-hidden="true"
            />
            {/* Waka-marae illustration on third panel.
                Zoomed in on mobile but kept centered at 250vw; desktop restored at md. */}
            <img
              ref={wakaMaraeRef}
              src={wakaMarae}
              alt=""
              className="absolute bottom-0 pointer-events-none opacity-10 w-[170vw] left-[165vw] md:w-[100vw] md:left-[200vw]"
              style={{ height: 'auto' }}
              aria-hidden="true"
            />
            {panels.map((panel, i) => {
              return (
                <div
                  key={panel.title}
                  ref={(el) => { panelEls.current[i] = el; }}
                  className="w-screen h-screen flex items-center justify-center shrink-0"
                >
                  <div className="max-w-4xl text-center px-6">
                    {/* Icon — parallax: deeper, slower */}
                    {panel.image && (
                      <div
                        className="mb-6 ps-icon"
                        aria-hidden="true"
                      >
                        <img
                          src={panel.image}
                          alt=""
                          className="h-20 w-20 mx-auto"
                        />
                      </div>
                    )}

                    {/* Text — parallax: shallower, faster */}
                    <div className="ps-text">
                      <h2
                        data-title
                        className="font-heading text-2xl md:text-3xl font-bold mb-4"
                        style={{ color: panel.accentColor }}
                      >
                        {panel.title}
                      </h2>
                      <p
                        data-body
                        className="font-mono text-sm md:text-base leading-relaxed"
                      >
                        {panel.description === 'matou-custom' ? (
                          <>
                            Mātou Collective is an{' '}
                            <a
                              href="https://docs.matou.nz/operations/operations-manual"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline hover:opacity-80 transition-opacity"
                              style={{ color: panel.accentColor }}
                            >
                              unincorporated indigenous-led technology collective
                            </a>{' '}
                            that builds digital tools rooted in Indigenous values, for collective wellbeing, community ownership, and self-determination.
                          </>
                        ) : (
                          panel.description
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Progress bar */}
          <div ref={progressBarRef} className="absolute bottom-0 left-0 right-0 h-1 z-50 transition-opacity duration-300">
            <div ref={progressRef} className="h-full" style={{ width: '0%' }} />
          </div>

          {/* Progress dots */}
          <div ref={dotsContainerRef} className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-50 transition-opacity duration-300">
            {panels.map((_, i) => (
              <div
                key={i}
                ref={(el) => { dotsRef.current[i] = el; }}
                className="w-2.5 h-2.5 rounded-full cursor-pointer transition-all duration-300"
                onClick={() => scrollToPanel(i)}
              />
            ))}
          </div>

          {/* Scroll hint */}
          <div
            ref={hintRef}
            className="absolute bottom-16 left-1/2 -translate-x-1/2 font-mono text-xs z-50 transition-opacity duration-500"
          >
            ↕ Scroll to navigate
          </div>
        </div>
      </section>

      {/* Research to Reality + VMOST Framework follows on white background */}
      <section className="py-16 md:py-24 bg-matou-bg">
        <div className="max-w-content mx-auto px-4 md:px-8">
          <div className="min-h-screen flex flex-col justify-center">
            <ResearchToReality />
          </div>
          <VMOSTFramework />
        </div>
      </section>
    </>
  );
}
